import React from 'react';
import { useEffect, useState } from 'react';
import { I18n } from '@aws-amplify/core';
import { useQueryClient } from '@tanstack/react-query';
import { Row, Col, Collapse, Skeleton, Form } from 'antd';

import { Video } from './Video';
import { Header } from './Header';
import { Result } from './Result';
import { CategoryOptions } from './Category';
import { OWAS_QUERY_KEYS } from '../../../../enums';
import { useOWASContext } from '../../../../context';
import { Load, ManualTools, TaskName } from './Fields';
import { useOwasEvaluationContext } from '../../context';
import { useGetOwasCategories } from '../../../../hooks/useGetOwasCategories';
import { useUpdateOwasFrame } from '../../../../hooks/useUpdateOwasFrame';
import type { OwasFrame } from '../../../../hooks/useGetOwasFrames';
import * as S from '../styles';

const { useFormInstance, useWatch } = Form;

interface FrameCollapseProps {
	frame: OwasFrame;
	video_url?: string;
	activeCollapse?: string;
	onClick(key?: string | string[]): void;
	onVideoError?: () => void;
}

export function FrameCollapse({
	frame,
	video_url,
	activeCollapse,
	onClick,
	onVideoError
}: Readonly<FrameCollapseProps>): JSX.Element {
	const form = useFormInstance();
	const queryClient = useQueryClient();
	const { data, parameters } = useOWASContext();
	const { stepFormName } = useOwasEvaluationContext();
	const { mutateAsync: updateFrame } = useUpdateOwasFrame();
	const { isLoading, data: categories, isError } = useGetOwasCategories();

	const [updated, setUpdated] = useState(false);
	const [isFirstRender, setIsFirstRender] = useState<boolean>(!!frame.code);

	const title = `Frame ${frame.sequence}`;
	const fieldName = [stepFormName, frame.id];

	const manualTools = useWatch([...fieldName, 'manual_tools'], form);
	const upperLimbs = useWatch([...fieldName, 'upper_limbs'], form);
	const lowerLimbs = useWatch([...fieldName, 'lower_limbs'], form);
	const taskId = useWatch([...fieldName, 'task_id'], form);
	const torso = useWatch([...fieldName, 'torso'], form);
	const load = useWatch([...fieldName, 'load'], form);

	useEffect(() => {
		if (manualTools && taskId && load && upperLimbs && lowerLimbs && torso) {
			setUpdated(true);
		}
	}, [manualTools, taskId, load, upperLimbs, lowerLimbs, torso]);

	useEffect(() => {
		async function updateFrameEvaluation() {
			await updateFrame({
				organization_id: parameters?.organization_id!,
				company_id: parameters?.company_id!,
				file_id: parameters?.file_id!,
				report_id: data?.id,
				frame_id: frame.id,
				load,
				torso,
				owas_task_id: taskId,
				lower_limbs: lowerLimbs,
				upper_limbs: upperLimbs,
				manual_tools: manualTools
			});
			queryClient.invalidateQueries([
				OWAS_QUERY_KEYS.GET_RESULT,
				{
					organization_id: parameters?.organization_id!,
					company_id: parameters?.company_id!,
					file_id: parameters?.file_id!,
					report_id: data?.id!,
					frame_id: frame.id
				}
			]);
			queryClient.invalidateQueries([OWAS_QUERY_KEYS.GET_TASKS]);
			setUpdated(false);
		}
		if (updated) {
			if (isFirstRender) {
				setIsFirstRender(false);
				setUpdated(false);
				return;
			}
			updateFrameEvaluation();
		}
	}, [updated, isFirstRender]);

	function renderHeader(): JSX.Element {
		return <Header id={frame.id} title={title} isOpen={activeCollapse === frame.id} />;
	}

	useEffect(() => {
		form.setFieldValue([...fieldName, 'manual_tools'], frame?.owas_task?.manual_tools ?? '');
		form.setFieldValue([...fieldName, 'manual_tools_input'], frame?.owas_task?.manual_tools ?? '');
		form.setFieldValue([...fieldName, 'task_id'], frame?.owas_task_id);
		frame.results.forEach((result) => {
			form.setFieldValue([...fieldName, result.owas_category.name], result.owas_sub_category_id);
		});
	}, [frame]);

	if (isLoading) {
		return <Skeleton active />;
	}

	if (isError) {
		return <span>{I18n.get('Oops... Something went wrong!')}</span>;
	}

	return (
		<Col span={24}>
			<S.CollapseWrapper accordion activeKey={activeCollapse} onChange={onClick}>
				<Collapse.Panel key={frame.id} header={renderHeader()}>
					<Row gutter={[0, 30]}>
						<Col span={24}>
							<Row gutter={[30, 30]}>
								<Col lg={24} xxl={10}>
									<Video url={video_url} frame={frame.frame} onError={onVideoError} />
								</Col>
								<Col lg={24} xxl={14}>
									<Row gutter={[20, 0]}>
										<Col span={12}>
											<TaskName formName={fieldName} />
										</Col>
										<Col span={12}>
											<Load formName={fieldName} />
										</Col>
										<Col span={24}>
											<ManualTools formName={fieldName} />
										</Col>
									</Row>
								</Col>
							</Row>
						</Col>
						<Col span={24}>
							<Row gutter={[0, 30]}>
								{categories.map((category) => (
									<Col key={category.id} span={24}>
										<CategoryOptions fieldName={fieldName} data={category} />
									</Col>
								))}
								<Col span={24}>
									<Result frameId={frame.id} />
								</Col>
							</Row>
						</Col>
					</Row>
				</Collapse.Panel>
			</S.CollapseWrapper>
		</Col>
	);
}
